import { useContext, useEffect, useLayoutEffect, useState } from "react";

import { useRouter } from "next/router";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { ConsentModeContext } from "@/common/contexts/ConsentModeContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { SignUpSessionStorage } from "@/features/Auth/Register/store/SignUpSessionStorage";
import { RegisterExperimentVariant } from "@/features/Auth/Register/store/types";

type RegisterExperimentOverrides = Record<string, string>;

const DEFAULT_VARIANTS = ["control", "test"] as const;

interface RegisterExperimentOptions<Variant extends string = RegisterExperimentVariant> {
  enabled?: boolean;
  name?: string;
  allocationVariants?: readonly [Variant, ...Variant[]];
}

const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

const getCypressExperimentOverride = (experimentKey: string, defaultVariant: string): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }
  if (!(window as Window & { Cypress?: unknown }).Cypress) {
    return undefined;
  }
  const overrides = (window as Window & { __REGISTER_EXPERIMENTS__?: RegisterExperimentOverrides })
    .__REGISTER_EXPERIMENTS__;
  return overrides?.[experimentKey] ?? defaultVariant;
};

export function useRegisterExperiment(
  experimentKey: string,
  options?: RegisterExperimentOptions
): RegisterExperimentVariant | null;
export function useRegisterExperiment<Variant extends string>(
  experimentKey: string,
  options: RegisterExperimentOptions<Variant> & { variants: readonly [Variant, ...Variant[]] }
): Variant | null;
export function useRegisterExperiment(
  experimentKey: string,
  options?: RegisterExperimentOptions<string> & { variants?: readonly [string, ...string[]] }
): string | null {
  const enabled = options?.enabled ?? true;
  const experimentName = options?.name;
  const variants = options?.variants ?? DEFAULT_VARIANTS;
  const allocationVariants = options?.allocationVariants ?? variants;
  const router = useRouter();
  const { logAmplitudeEvent } = useAmplitude();
  const { hasFunctionalityConsentMode } = useContext(ConsentModeContext);
  const [variant, setVariant] = useState<string | null>(null);

  useIsomorphicLayoutEffect(() => {
    const requestedCypressVariant = getCypressExperimentOverride(experimentKey, variants[0]);
    if (requestedCypressVariant) {
      const cypressOverride = variants.includes(requestedCypressVariant) ? requestedCypressVariant : variants[0];
      SignUpSessionStorage.setRegisterExperiment(experimentKey, { variant: cypressOverride, exposed: true });
      setVariant(cypressOverride);
      return;
    }

    const forcedVariant = new URLSearchParams(window.location.search).get(experimentKey);
    if (forcedVariant && variants.includes(forcedVariant)) {
      SignUpSessionStorage.setRegisterExperiment(experimentKey, {
        variant: forcedVariant,
        exposed: true,
        isOverride: true,
      });
      setVariant(forcedVariant);
      return;
    }

    const saved = SignUpSessionStorage.getRegisterExperiment(experimentKey);
    if (saved?.isOverride && variants.includes(saved.variant)) {
      setVariant(saved.variant);
      return;
    }

    const isEnglishVisitor = router.locale?.toLowerCase().startsWith("en") ?? true;
    if (!isEnglishVisitor) {
      return;
    }

    const stored = saved && allocationVariants.includes(saved.variant) ? saved : undefined;

    if (!enabled) {
      if (stored) {
        setVariant(stored.variant);
      }
      return;
    }

    const rolled = stored?.variant ?? allocationVariants[Math.floor(Math.random() * allocationVariants.length)];

    if (!stored?.exposed) {
      if (hasFunctionalityConsentMode) {
        logAmplitudeEvent(AmplitudeEvents.ExperimentRegister, {
          experiment_key: experimentKey,
          variant: rolled,
          ...(experimentName ? { experiment_name: experimentName } : {}),
        });
      }
      SignUpSessionStorage.setRegisterExperiment(experimentKey, {
        variant: rolled,
        exposed: hasFunctionalityConsentMode,
      });
    }

    setVariant(rolled);
  }, [
    experimentKey,
    experimentName,
    enabled,
    variants,
    allocationVariants,
    router.locale,
    logAmplitudeEvent,
    hasFunctionalityConsentMode,
  ]);

  return variant;
}
